if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name="id"]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification') || document.querySelector('cart-items');
      this.useSuccessMessage = this.cart && this.cart.tagName.toLowerCase() !== 'cart-notification' && window.showAddToCartMessage;
      this.submitButton = this.querySelector('[type="submit"]');
      if(this.submitButton)
        this.submitButtonText = this.submitButton.querySelector('span');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this?.submitButton?.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      if (this.dataset.cartAddOnAction === 'replace') {
        this.replaceNestedCartAddOn();
        return;
      }
      if (this.dataset.cartAddOnAction === 'add') {
        this.addNestedCartAddOns();
        return;
      }
      if (!this.validateAddOns()) return;
      const quantitySet = this.getQuantitySet();
      if (quantitySet && !quantitySet.validate()) {
        this.handleErrorMessage(quantitySet.validationMessage);
        return;
      }

      this?.submitButton?.setAttribute('aria-disabled', true);
      this?.submitButton?.classList.add('loading');
      this?.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      const request = await this.prepareRequestConfig();
      const formData = request.formData;

      const addToCart = async () => {
        let response;
        for (const config of request.configs || [request.config]) {
          response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());
          if (response.status) break;
        }
        return response;
      };

      addToCart()
        .then((response) => {
          if (response.status && response.status != 422) {
            document.dispatchEvent(new CustomEvent('afterAddCartError', {detail: {
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
              target: this
            }}));
            this.handleErrorMessage(response.description);
            const soldOutMessage = this?.submitButton?.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this?.submitButton?.setAttribute('aria-disabled', true);
            this?.submitButton?.querySelector('span')?.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if(!this.error) {
            if(response.status != 422) {
              this.error = false;
              if(this.useSuccessMessage) {
                pushSuccessMessage(window.cartStrings.success);
              }
              this.completeAddToCartAction(response, formData.get('id'));
            } else {
              this.handleErrorMessage(response.description);
              this.handle422Error(formData.get('id'));
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this?.submitButton?.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this?.submitButton?.removeAttribute('aria-disabled');
          this?.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        });
    }

    async prepareRequestConfig() {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const formData = new FormData(this.form);
      const quantitySet = this.getQuantitySet();
      if (quantitySet?.usesFiveVerseCart?.()) {
        const requestBodies = quantitySet.fiveVerseCartBodies(formData);
        formData.set('id', String(requestBodies[0].items[0].id));
        formData.set('quantity', String(quantitySet.quantity));
        if (this.cart) {
          const finalBody = requestBodies[requestBodies.length - 1];
          finalBody.sections = [...new Set(this.cart.getSectionsToRender().map(section => section.id))];
          finalBody.sections_url = `${window.location.pathname}${window.location.search}`;
          this.cart.setActiveElement(document.activeElement);
        }
        const configs = requestBodies.map((body) => ({...config, headers: {...config.headers}, body: JSON.stringify(body)}));
        return { config: configs[0], configs, formData };
      }
      if (quantitySet?.usesPerItemFormData?.()) {
        const perItemForms = quantitySet.cartFormData(formData);
        formData.set('id', String(quantitySet.items()[0].id));
        formData.set('quantity', String(quantitySet.quantity));
        delete config.headers['Content-Type'];
        if (this.cart) {
          const finalForm = perItemForms[perItemForms.length - 1];
          finalForm.append('sections', [...new Set(this.cart.getSectionsToRender().map(section => section.id))].join(','));
          finalForm.append('sections_url', `${window.location.pathname}${window.location.search}`);
          this.cart.setActiveElement(document.activeElement);
        }
        const configs = perItemForms.map((itemForm) => ({...config, headers: {...config.headers}, body: itemForm}));
        return { config: configs[0], configs, formData };
      }
      if (quantitySet?.usesMixedItems()) {
        const requestBody = { items: quantitySet.cartItems(formData) };
        formData.set('id', String(requestBody.items[0].id));
        formData.set('quantity', String(quantitySet.quantity));
        if (this.cart) {
          requestBody.sections = [...new Set(this.cart.getSectionsToRender().map(section => section.id))];
          requestBody.sections_url = `${window.location.pathname}${window.location.search}`;
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = JSON.stringify(requestBody);
        return { config, formData };
      }
      const hasAddOnGroups = this.form.querySelector('[data-add-on-group]') != null;
      const selectedAddOns = this.getSelectedAddOns(formData);
      this.removeAddOnFields(formData);
      if (selectedAddOns.length > 0) {
        const requestBody = await this.prepareNestedAddOnsBody(formData, selectedAddOns);

        if (this.cart) {
          requestBody.sections = this.cart.getSectionsToRender().map((section) => section.id);
          requestBody.sections = [...new Set(requestBody.sections)];
          requestBody.sections_url = `${window.location.pathname}${window.location.search}`;
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(requestBody);

        return {
          config: config,
          formData: formData
        };
      }

      delete config.headers['Content-Type'];
      if (hasAddOnGroups) {
        const properties = await this.resolveAddOnParentProperties(formData, []);
        this.setFormDataProperties(formData, properties);
      }
      if (this.cart) {
        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        const uniqueSections = [...new Set(sections)];
        formData.append('sections', uniqueSections.join(','));
        formData.append('sections_url', `${window.location.pathname}${window.location.search}`);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      return {
        config: config,
        formData: formData
      };
    }

    getQuantitySet() {
      const mainSet = this.form.closest('.oc-purchase')?.querySelector('orocuff-quantity-cards');
      if (mainSet && !mainSet.hidden) return mainSet;
      const sticky = this.closest('sticky-cart');
      const stickySet = sticky && document.getElementById(sticky.dataset.form)?.querySelector('orocuff-quantity-cards');
      return stickySet && !stickySet.hidden ? stickySet : null;
    }

    removeAddOnFields(formData) {
      [...formData.keys()]
        .filter((key) => key.startsWith('add_on_'))
        .forEach((key) => formData.delete(key));
    }

    async replaceNestedCartAddOn() {
      const formData = new FormData(this.form);
      const variantId = formData.get('id');
      const previousVariantId = this.dataset.cartAddOnVariantId;
      const previousLineKey = this.dataset.cartAddOnKey;
      const parentLineKey = this.dataset.cartAddOnParentKey;
      const requestedQuantity = this.getParentQuantity(formData, this.dataset.cartAddOnQuantity);
      const previousQuantity = parseInt(this.dataset.cartAddOnQuantity || '1');
      const variantChanged = `${variantId}` !== `${previousVariantId}`;

      if (!variantId || !previousLineKey || !parentLineKey) {
        this.handleErrorMessage(window.cartStrings.error);
        return;
      }

      if (!variantChanged && requestedQuantity === previousQuantity) {
        this.closest('quick-add-drawer')?.close();
        return;
      }

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      let addedLineKey;
      try {
        if (!variantChanged) {
          const changedState = await this.changeCartLine(previousLineKey, requestedQuantity);
          this.error = false;
          this.completeCartAddOnReplacement(changedState, variantId);
          return;
        }

        const currentParentLineKey = await this.updateAddOnParentConfiguration(
          parentLineKey,
          [{ id: variantId, label: this.dataset.cartAddOnLabel }],
          previousVariantId
        );
        const addResponse = await fetch(`${routes.cart_add_url}`, {
          ...fetchConfig('javascript'),
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: requestedQuantity,
              parent_line_key: currentParentLineKey
            }]
          })
        });
        const addedState = await addResponse.json();
        if (!addResponse.ok || addedState.status) {
          throw new Error(addedState.description || addedState.message || window.cartStrings.error);
        }
        addedLineKey = addedState.items?.[0]?.key;

        const cartResponse = await fetch(`${routes.cart_url}.js`, fetchConfig('json', 'GET'));
        const cartState = await cartResponse.json();
        if (!cartResponse.ok) throw new Error(window.cartStrings.error);

        const addedLine = cartState.items.find((item) => item.key === addedLineKey)
          || cartState.items.find((item) => `${item.variant_id}` === `${variantId}` && item.parent_relationship);
        const currentParentKey = addedLine?.parent_relationship?.parent_key || currentParentLineKey;
        const previousLine = cartState.items.find((item) => item.key === previousLineKey)
          || cartState.items.find((item) => (
            `${item.variant_id}` === `${previousVariantId}`
            && item.parent_relationship?.parent_key === currentParentKey
          ));
        if (!previousLine) throw new Error(window.cartStrings.error);

        const changedState = await this.changeCartLine(previousLine.key, 0);

        this.error = false;
        this.completeCartAddOnReplacement(changedState, variantId);
      } catch (error) {
        this.error = true;
        this.handleErrorMessage(error.message || window.cartStrings.error);
        console.error(error);
        if (addedLineKey) this.rollbackAddedCartLine(addedLineKey);
      } finally {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      }
    }

    async addNestedCartAddOns() {
      if (!this.validateAddOns()) return;

      const parentLineKey = this.dataset.cartAddOnParentKey;
      const formData = new FormData(this.form);
      formData.set('quantity', this.dataset.cartAddOnParentQuantity || '1');
      const existingProductIds = new Set(
        (this.dataset.cartAddOnExistingProductIds || '').split(',').filter(Boolean)
      );
      const selectedAddOns = this.getSelectedAddOns(formData)
        .filter((addOn) => !existingProductIds.has(`${addOn.productId}`));

      if (!parentLineKey || selectedAddOns.length === 0) {
        this.handleErrorMessage(window.variantStrings?.chooseOptions || window.cartStrings.error);
        return;
      }

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      try {
        const currentParentLineKey = await this.updateAddOnParentConfiguration(
          parentLineKey,
          selectedAddOns
        );
        const addResponse = await fetch(`${routes.cart_add_url}`, {
          ...fetchConfig('javascript'),
          body: JSON.stringify({
            items: selectedAddOns.map((addOn) => ({
              id: addOn.id,
              quantity: addOn.quantity,
              parent_line_key: currentParentLineKey
            })),
            sections: this.getCartSectionsToRender(),
            sections_url: `${window.location.pathname}${window.location.search}`
          })
        });
        const addedState = await addResponse.json();
        if (!addResponse.ok || addedState.status) {
          throw new Error(addedState.description || addedState.message || window.cartStrings.error);
        }

        this.error = false;
        this.completeCartAddOnReplacement(addedState, selectedAddOns[0].id);
      } catch (error) {
        this.error = true;
        this.handleErrorMessage(error.message || window.cartStrings.error);
        console.error(error);
      } finally {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      }
    }

    async updateAddOnParentConfiguration(parentLineKey, addedAddOns = [], replacedVariantId = null) {
      const cartResponse = await fetch(`${routes.cart_url}.js`, fetchConfig('json', 'GET'));
      const cartState = await cartResponse.json();
      if (!cartResponse.ok) throw new Error(window.cartStrings.error);

      const parentLine = cartState.items.find((item) => item.key === parentLineKey);
      if (!parentLine) throw new Error(window.cartStrings.error);

      const existingAddOns = cartState.items
        .filter((item) => item.parent_relationship?.parent_key === parentLineKey)
        .filter((item) => replacedVariantId == null || `${item.variant_id}` !== `${replacedVariantId}`)
        .map((item) => ({
          id: item.variant_id,
          label: this.getCartAddOnLabel(item)
        }));
      const addOns = [...existingAddOns, ...addedAddOns];
      const configuration = this.createAddOnConfiguration(addOns.map((addOn) => addOn.id));

      const properties = this.getPublicLineItemProperties(parentLine.properties);
      const comparableParentLines = this.getComparableParentLines(
        cartState,
        parentLine.variant_id,
        parentLine.selling_plan_allocation?.selling_plan?.id,
        properties,
        parentLine.key
      );
      const matchingParentLine = comparableParentLines.find((item) => (
        this.getCartLineAddOnConfiguration(cartState, item.key) === configuration
      ));

      if (matchingParentLine?.properties?._add_on) {
        properties._add_on = matchingParentLine.properties._add_on;
      } else if (matchingParentLine?.properties?._add_on_configuration) {
        properties._add_on_configuration = matchingParentLine.properties._add_on_configuration;
      } else if (!matchingParentLine && comparableParentLines.length > 0) {
        properties._add_on = this.createAddOnPropertyValue(addOns);
      }

      if (this.lineItemPropertiesEqual(parentLine.properties, properties)) return parentLine.key;

      const changeResponse = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: parentLine.key,
          quantity: parentLine.quantity,
          properties
        })
      });
      const changedState = await changeResponse.json();
      if (!changeResponse.ok || changedState.errors || changedState.status) {
        throw new Error(changedState.description || changedState.message || changedState.errors || window.cartStrings.error);
      }

      const updatedParentLine = changedState.items.find((item) => (
        item.parent_relationship == null
        && `${item.variant_id}` === `${parentLine.variant_id}`
        && `${item.selling_plan_allocation?.selling_plan?.id || ''}`
          === `${parentLine.selling_plan_allocation?.selling_plan?.id || ''}`
        && Object.entries(properties).every(([key, value]) => item.properties?.[key] === value)
        && Object.keys(item.properties || {}).every((key) => properties[key] === item.properties[key])
      ));
      if (!updatedParentLine) throw new Error(window.cartStrings.error);

      return updatedParentLine.key;
    }

    async changeCartLine(lineKey, quantity) {
      const changeResponse = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: lineKey,
          quantity,
          sections: this.getCartSectionsToRender(),
          sections_url: `${window.location.pathname}${window.location.search}`
        })
      });
      const changedState = await changeResponse.json();
      if (!changeResponse.ok || changedState.errors || changedState.status) {
        throw new Error(changedState.description || changedState.message || changedState.errors || window.cartStrings.error);
      }
      return changedState;
    }

    getCartSectionsToRender() {
      const renderers = [this.cart, document.querySelector('cart-items')].filter(Boolean);
      return [...new Set(renderers.flatMap((renderer) => renderer.getSectionsToRender().map((section) => section.id)))];
    }

    rollbackAddedCartLine(lineKey) {
      fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      }).catch((error) => console.error(error));
    }

    completeCartAddOnReplacement(response, variantId) {
      const renderCart = () => {
        const renderers = [this.cart, document.querySelector('cart-items')].filter(Boolean);
        [...new Set(renderers)].forEach((renderer) => renderer.renderContents(response));
      };
      const quickAddDrawer = this.closest('quick-add-drawer');

      if (quickAddDrawer) {
        document.body.addEventListener('drawerClosed', () => setTimeout(renderCart), { once: true });
        quickAddDrawer.close();
      } else {
        renderCart();
      }

      document.dispatchEvent(new CustomEvent('afterCartChanged', {detail: {
        productVariantId: variantId,
        source: 'cart-add-on',
        cartData: response,
        target: this
      }}));
    }

    getSelectedAddOns(formData) {
      const parentQuantity = this.getParentQuantity(formData);
      return [...this.form.querySelectorAll('.product-add-ons__input:checked')]
        .filter((input) => input.dataset.addOnVariantId)
        .filter((input) => this.isAddOnGroupActive(input.closest('[data-add-on-group]')))
        .map((input) => {
          return {
            id: input.dataset.addOnVariantId,
            productId: input.dataset.addOnProductId,
            label: this.createAddOnLabel(
              input.closest('[data-add-on-group]')?.dataset.addOnDisplayName,
              input.dataset.addOnVariantTitle
            ),
            quantity: this.getAddOnQuantity(input, parentQuantity)
          };
        })
        .filter((item) => item.id && item.quantity > 0);
    }

    getAddOnQuantity(input, parentQuantity = 1) {
      if (input.closest('[data-add-on-group]')?.dataset.addOnMatchParentQuantity === 'true') {
        return parentQuantity;
      }

      const min = parseInt(input.dataset.addOnQuantityMin || '1');
      const increment = parseInt(input.dataset.addOnQuantityIncrement || '1');
      const max = parseInt(input.dataset.addOnQuantityMax || '');
      const safeMin = Number.isNaN(min) || min < 1 ? 1 : min;
      const safeIncrement = Number.isNaN(increment) || increment < 1 ? 1 : increment;
      let quantity = safeMin;

      if ((quantity - safeMin) % safeIncrement !== 0) {
        quantity = safeMin + Math.ceil((quantity - safeMin) / safeIncrement) * safeIncrement;
      }

      if (!Number.isNaN(max) && max > 0 && quantity > max) quantity = max;

      return quantity;
    }

    isAddOnGroupActive(group) {
      if (!group) return true;
      const toggle = group.querySelector('.product-add-ons__toggle');
      return !toggle || toggle.checked;
    }

    getParentQuantity(formData, fallbackQuantity = 1) {
      const quantity = parseInt(formData.get('quantity') || fallbackQuantity);
      return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    async prepareNestedAddOnsBody(formData, selectedAddOns) {
      const parentId = formData.get('id');
      const parentItem = {
        id: parentId,
        quantity: this.getParentQuantity(formData)
      };
      const sellingPlan = formData.get('selling_plan');
      const properties = await this.resolveAddOnParentProperties(
        formData,
        selectedAddOns
      );

      if (sellingPlan) parentItem.selling_plan = sellingPlan;
      if (Object.keys(properties).length > 0) parentItem.properties = properties;

      return {
        items: [
          parentItem,
          ...selectedAddOns.map((addOn) => ({
            id: addOn.id,
            quantity: addOn.quantity,
            parent_id: parentId
          }))
        ]
      };
    }

    createAddOnConfiguration(variantIds) {
      return variantIds.map((variantId) => `${variantId}`).sort().join(',');
    }

    createAddOnLabel(displayName, variantTitle) {
      if (!variantTitle || variantTitle === 'Default Title') return displayName;
      return `${displayName} (${variantTitle})`;
    }

    createAddOnPropertyValue(addOns) {
      const labels = [...addOns]
        .filter((addOn) => addOn.label)
        .sort((first, second) => `${first.id}`.localeCompare(`${second.id}`))
        .map((addOn) => addOn.label);
      return labels.join('; ') || 'None';
    }

    getCartAddOnLabel(item) {
      const line = document.querySelector(`[data-cart-line-key="${CSS.escape(item.key)}"]`);
      if (line?.dataset.addOnLabel) return line.dataset.addOnLabel;
      return this.createAddOnLabel(item.product_title, item.variant_title);
    }

    async resolveAddOnParentProperties(formData, addOns) {
      const properties = this.getLineItemProperties(formData);
      const configuration = this.createAddOnConfiguration(addOns.map((addOn) => addOn.id));

      try {
        const cartResponse = await fetch(`${routes.cart_url}.js`, fetchConfig('json', 'GET'));
        const cartState = await cartResponse.json();
        if (!cartResponse.ok) throw new Error(window.cartStrings.error);

        const comparableParentLines = this.getComparableParentLines(
          cartState,
          formData.get('id'),
          formData.get('selling_plan'),
          properties
        );
        if (comparableParentLines.length === 0) return properties;

        const matchingParentLine = comparableParentLines.find((item) => (
          this.getCartLineAddOnConfiguration(cartState, item.key) === configuration
        ));
        if (matchingParentLine) {
          if (matchingParentLine.properties?._add_on) {
            properties._add_on = matchingParentLine.properties._add_on;
          } else if (matchingParentLine.properties?._add_on_configuration) {
            properties._add_on_configuration = matchingParentLine.properties._add_on_configuration;
          }
          return properties;
        }

        properties._add_on = this.createAddOnPropertyValue(addOns);
        return properties;
      } catch (error) {
        properties._add_on = this.createAddOnPropertyValue(addOns);
        console.error(error);
        return properties;
      }
    }

    getComparableParentLines(cartState, variantId, sellingPlanId, properties, excludedLineKey = null) {
      const normalizedSellingPlanId = `${sellingPlanId || ''}`;
      return cartState.items.filter((item) => (
        item.parent_relationship == null
        && item.key !== excludedLineKey
        && `${item.variant_id}` === `${variantId}`
        && `${item.selling_plan_allocation?.selling_plan?.id || ''}` === normalizedSellingPlanId
        && this.lineItemPropertiesEqual(this.getPublicLineItemProperties(item.properties), properties)
      ));
    }

    getCartLineAddOnConfiguration(cartState, parentLineKey) {
      return this.createAddOnConfiguration(
        cartState.items
          .filter((item) => item.parent_relationship?.parent_key === parentLineKey)
          .map((item) => item.variant_id)
      );
    }

    getPublicLineItemProperties(properties = {}) {
      properties = properties || {};
      return Object.fromEntries(
        Object.entries(properties).filter(([key, value]) => (
          key !== '_add_on_configuration'
          && key !== '_add_on'
          && key !== '_add_on_instance_id'
          && value !== ''
        ))
      );
    }

    lineItemPropertiesEqual(firstProperties = {}, secondProperties = {}) {
      firstProperties = firstProperties || {};
      secondProperties = secondProperties || {};
      const firstEntries = Object.entries(firstProperties);
      const secondEntries = Object.entries(secondProperties);
      return firstEntries.length === secondEntries.length
        && firstEntries.every(([key, value]) => secondProperties[key] === value);
    }

    setFormDataProperties(formData, properties) {
      [...formData.keys()]
        .filter((key) => key.startsWith('properties['))
        .forEach((key) => formData.delete(key));
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }

    getLineItemProperties(formData) {
      const properties = {};
      formData.forEach((value, key) => {
        if (!key.startsWith('properties[')) return;
        const propertyName = key.slice(11, -1);
        if (propertyName && value !== '') properties[propertyName] = value;
      });
      return properties;
    }

    validateAddOns() {
      let isValid = true;

      this.form.querySelectorAll('[data-add-on-group]').forEach((group) => {
        const selectedCount = group.querySelectorAll('.product-add-ons__input:checked[data-add-on-variant-id]').length;
        const min = parseInt(group.dataset.addOnGroupMin || '0');
        const max = parseInt(group.dataset.addOnGroupMax || '0');
        const toggle = group.querySelector('.product-add-ons__toggle');
        const groupActive = this.isAddOnGroupActive(group);
        const firstInput = toggle || group.querySelector('.product-add-ons__value-input') || group.querySelector('.product-add-ons__input');
        let message = '';

        if (groupActive && selectedCount < Math.max(min, toggle ? 1 : min)) {
          message = window.variantStrings?.chooseOptions || 'Please select an option.';
        } else if (groupActive && max > 0 && selectedCount > max) {
          message = window.cartStrings?.quantityError?.replace('[quantity]', max) || 'Please select fewer options.';
        }

        group.querySelectorAll('.product-add-ons__input').forEach((input) => input.setCustomValidity(''));
        group.querySelectorAll('.product-add-ons__value-input').forEach((input) => input.setCustomValidity(''));
        toggle?.setCustomValidity('');
        if (message && firstInput) {
          firstInput.setCustomValidity(message);
          firstInput.reportValidity();
          isValid = false;
        }
      });

      return isValid;
    }

    handle422Error(variantId) {
      const sections = this.cart.getSectionsToRender().map((section) => section.id);
      const uniqueSections = sections.filter((item, index) => sections.indexOf(item) === index);
      fetch(`${window.location.pathname}?sections=${uniqueSections.join(",")}`)
        .then((response) => response.json())
        .then(response => {
          const finalResponse = {
            sections: response
          };
          this.completeAddToCartAction(finalResponse, variantId);
        });
    }

    completeAddToCartAction(response, variantId) {
      const quickAddDrawer = this.closest('quick-add-drawer');
      if (quickAddDrawer) {
        document.body.addEventListener('drawerClosed', () => {
          setTimeout(() => { this.cart.renderContents(response) });
        }, { once: true });
        quickAddDrawer.close();
      } else {
        this.cart.renderContents(response);
      }
      document.dispatchEvent(new CustomEvent('afterCartChanged', {detail: {
        productVariantId: variantId,
        quantity: Number(this.form.querySelector('[name="quantity"]')?.value || 1),
        source: 'product-form',
        cartData: response,
        target: this
      }}));
    }

    toggleSubmitButton(disable = true, text) {
      if (disable) {
        this.submitButton.setAttribute('disabled', 'disabled');
        if (text && this.submitButtonText) this.submitButtonText.textContent = text;
      } else {
        this.submitButton.removeAttribute('disabled');
        if(this.submitButtonText) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if(errorMessage && !this.errorMessageWrapper) {
        pushErrorMessage(errorMessage);
      }
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
